import React, { useEffect, useRef, useState, useCallback } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ai, projects as projectsApi } from "../../lib/api";
import { PaintBrush, Eraser, PaintBucket, Eyedropper, Trash, FloppyDisk, Download, Sparkle } from "@phosphor-icons/react";
import { proceduralSprite, dataUrlToPixelGrid, quantizeToPalette, assetFileName } from "../../lib/gamecraft";
import { toast } from "sonner";

const PALETTE = [
  "#000000", "#1f1f29", "#3d3d4a", "#7c7c8a", "#c8c8d0", "#ffffff",
  "#e63946", "#f1a208", "#ffd60a", "#06d6a0", "#118ab2", "#22d3ee",
  "#7c3aed", "#ec4899", "#84cc16", "#fb7185", "#f97316", "#a16207",
];

const SIZES = [8, 16, 24, 32, 48, 64];

export default function SpriteEditorPanel({ project, onChange, studio }) {
  const { t } = useI18n();
  const kinds = studio?.kinds || [{ id: "sprite", label: "Sprite" }];
  const subtypes = studio?.subtypes || [];
  const [kind, setKind] = useState(kinds[0].id);
  const [subtype, setSubtype] = useState(subtypes[0]?.id || "");
  const canvasRef = useRef(null);
  const [grid, setGrid] = useState(studio?.size || 32);
  const [pixels, setPixels] = useState(() => Array(grid * grid).fill(null));
  const [color, setColor] = useState("#22d3ee");
  const [tool, setTool] = useState("brush");
  const [drawing, setDrawing] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [name, setName] = useState("sprite-1");
  const lockedPalette = project.config?.palette || [];
  const camera = project.config?.camera === "topdown" ? "top-down Enter the Gungeon view" : "side view Metal Slug platformer";

  const CELL = 14;

  useEffect(() => {
    setKind(kinds[0].id);
    setSubtype((studio?.subtypes || [])[0]?.id || "");
    if (studio?.size) setGrid(studio.size);
  }, [studio?.id]); // eslint-disable-line

  // Resize pixels when grid changes
  useEffect(() => {
    setPixels(Array(grid * grid).fill(null));
  }, [grid]);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0a0a0c";
    ctx.fillRect(0, 0, c.width, c.height);
    // checker
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < grid; x++) {
        const i = y * grid + x;
        if (pixels[i]) {
          ctx.fillStyle = pixels[i];
          ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
        } else if ((x + y) % 2 === 0) {
          ctx.fillStyle = "#111114";
          ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
        }
      }
    }
    // grid lines
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    for (let i = 0; i <= grid; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL + 0.5, 0);
      ctx.lineTo(i * CELL + 0.5, grid * CELL);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * CELL + 0.5);
      ctx.lineTo(grid * CELL, i * CELL + 0.5);
      ctx.stroke();
    }
  }, [pixels, grid]);

  useEffect(() => { draw(); }, [draw]);

  const pixelAt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / (rect.width / grid));
    const y = Math.floor((e.clientY - rect.top) / (rect.height / grid));
    if (x < 0 || y < 0 || x >= grid || y >= grid) return -1;
    return y * grid + x;
  };

  const floodFill = (startIdx) => {
    const target = pixels[startIdx];
    if (target === color) return pixels;
    const next = [...pixels];
    const stack = [startIdx];
    while (stack.length) {
      const idx = stack.pop();
      if (next[idx] !== target) continue;
      next[idx] = color;
      const x = idx % grid;
      const y = Math.floor(idx / grid);
      if (x > 0) stack.push(idx - 1);
      if (x < grid - 1) stack.push(idx + 1);
      if (y > 0) stack.push(idx - grid);
      if (y < grid - 1) stack.push(idx + grid);
    }
    return next;
  };

  const apply = (e) => {
    const idx = pixelAt(e);
    if (idx < 0) return;
    if (tool === "brush") {
      const next = [...pixels];
      next[idx] = color;
      setPixels(next);
    } else if (tool === "eraser") {
      const next = [...pixels];
      next[idx] = null;
      setPixels(next);
    } else if (tool === "bucket") {
      setPixels(floodFill(idx));
    } else if (tool === "picker") {
      if (pixels[idx]) setColor(pixels[idx]);
    }
  };

  const handleClear = () => setPixels(Array(grid * grid).fill(null));

  const buildPng = () => {
    const off = document.createElement("canvas");
    off.width = grid;
    off.height = grid;
    const ctx = off.getContext("2d");
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < grid; x++) {
        const c = pixels[y * grid + x];
        if (c) {
          ctx.fillStyle = c;
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
    return off.toDataURL("image/png");
  };

  const handleExport = () => {
    const dataUrl = buildPng();
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${name || "sprite"}.png`;
    a.click();
  };

  const handleSave = async () => {
    try {
      const dataUrl = buildPng();
      await projectsApi.addSprite(project.project_id, {
        name: assetFileName(kind, name, grid),
        data_url: dataUrl,
        category: kind,
        subtype: subtype || kind,
        tags: [kind, subtype].filter(Boolean),
        studio: studio?.id,
        width: grid,
        height: grid,
      });
      toast.success(t("ide.saved"));
      const p = await projectsApi.get(project.project_id);
      onChange({ sprites: p.sprites });
    } catch {
      toast.error(t("toast.saveFailed"));
    }
  };

  const paintFromUrl = async (url) => {
    let cells = await dataUrlToPixelGrid(url, grid);
    if (lockedPalette.length) cells = quantizeToPalette(cells, lockedPalette);
    setPixels(cells);
  };

  const handleAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await ai.sprite({
        prompt: `${studio?.ai || "pixel art sprite"}. Camera: ${camera}. Type: ${kind}. Size ${grid}x${grid}. ${aiPrompt}`,
        style: studio?.ai || "pixel art",
        size: grid,
      });
      const raw = res.data || "";
      const url = raw.startsWith("data:") ? raw : `data:${res.mime_type || "image/png"};base64,${raw}`;
      await paintFromUrl(url);
      if (res.source === "gemini") {
        toast.success(`Sprite ${grid}×${grid} en el canvas — edítalo ya`);
      } else if (res.source === "pollinations") {
        toast.message(`Respaldo de imagen pintado a ${grid}×${grid}. Gemini no respondió.`);
      } else {
        if (res.needs_api_key) toast.error("Añade tu Google API key en Ajustes.");
        else toast.message("Sin motor de imagen. Plantilla procedural en el canvas.");
      }
    } catch (e) {
      const url = proceduralSprite(`${kind} ${aiPrompt}`, grid);
      await paintFromUrl(url).catch(() => {});
      toast.error(e?.response?.data?.detail || t("toast.aiFailed"));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="p-6 flex flex-col lg:flex-row gap-6">
      {/* Canvas Column */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-1 border border-zinc-800">
            {[
              { id: "brush", icon: PaintBrush, label: t("sprite.tools.brush") },
              { id: "eraser", icon: Eraser, label: t("sprite.tools.eraser") },
              { id: "bucket", icon: PaintBucket, label: t("sprite.tools.bucket") },
              { id: "picker", icon: Eyedropper, label: t("sprite.tools.picker") },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                data-testid={`sprite-tool-${id}`}
                onClick={() => setTool(id)}
                title={label}
                className={
                  "w-9 h-9 flex items-center justify-center transition-colors " +
                  (tool === id ? "bg-cyan-400 text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900")
                }
              >
                <Icon size={16} weight={tool === id ? "fill" : "regular"} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 text-xs font-mono px-2 py-1.5 text-zinc-200"
            >
              {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
            {subtypes.length > 0 && (
              <select
                value={subtype}
                onChange={(e) => setSubtype(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 text-xs font-mono px-2 py-1.5 text-zinc-200"
                title="Tipo concreto"
              >
                {subtypes.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            )}
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{t("sprite.size")}</div>
            <div className="flex gap-px bg-zinc-800">
              {SIZES.map((s) => (
                <button
                  key={s}
                  data-testid={`sprite-size-${s}`}
                  onClick={() => setGrid(s)}
                  className={
                    "px-2 py-1 text-[11px] font-mono transition-colors " +
                    (grid === s ? "bg-cyan-400 text-zinc-950" : "bg-zinc-950 text-zinc-300 hover:bg-zinc-900")
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-4 inline-block">
          <canvas
            ref={canvasRef}
            width={grid * CELL}
            height={grid * CELL}
            data-testid="sprite-canvas"
            onMouseDown={(e) => { setDrawing(true); apply(e); }}
            onMouseUp={() => setDrawing(false)}
            onMouseLeave={() => setDrawing(false)}
            onMouseMove={(e) => { if (drawing) apply(e); }}
            className="cursor-crosshair block"
            style={{ imageRendering: "pixelated" }}
          />
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <div className="flex gap-px bg-zinc-800 border border-zinc-800">
            {PALETTE.map((c) => (
              <button
                key={c}
                data-testid={`palette-${c}`}
                onClick={() => setColor(c)}
                className={"w-6 h-6 transition-transform " + (color === c ? "ring-2 ring-cyan-400 z-10" : "")}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        {lockedPalette.length > 0 && (
          <div className="flex gap-px bg-zinc-800 border border-zinc-800 ml-2">
            {lockedPalette.map((c) => (
              <button key={c} onClick={() => setColor(c)} className={"w-6 h-6 " + (color === c ? "ring-2 ring-cyan-400 z-10" : "")} style={{ backgroundColor: c }} title="Paleta del proyecto" />
            ))}
          </div>
        )}
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            data-testid="custom-color-picker"
            className="w-9 h-9 bg-transparent border border-zinc-800 cursor-pointer"
          />
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Input
            data-testid="sprite-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="sprite name"
            className="bg-zinc-900 border-zinc-800 rounded-none text-xs font-mono w-48"
          />
          <Button data-testid="sprite-save-btn" onClick={handleSave} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-2">
            <FloppyDisk size={14} /> {t("sprite.save")}
          </Button>
          <Button data-testid="sprite-export-btn" onClick={handleExport} variant="ghost" className="rounded-none text-zinc-300 border border-zinc-800 hover:bg-zinc-900 gap-2">
            <Download size={14} /> {t("sprite.export")}
          </Button>
          <Button data-testid="sprite-clear-btn" onClick={handleClear} variant="ghost" className="rounded-none text-red-400 border border-zinc-800 hover:bg-zinc-900 gap-2">
            <Trash size={14} /> {t("sprite.clear")}
          </Button>
        </div>
      </div>

      {/* AI Column */}
      <div className="lg:w-80 border-l border-zinc-800 lg:pl-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400 mb-4 flex items-center gap-2">
          <Sparkle size={14} weight="fill" /> AI SPRITE
        </div>
        <Input
          data-testid="sprite-ai-prompt"
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          placeholder={t("sprite.ai.placeholder")}
          className="bg-zinc-900 border-zinc-800 rounded-none font-mono text-sm mb-3"
        />
        <Button
          data-testid="sprite-ai-generate-btn"
          onClick={handleAI}
          disabled={aiLoading || !aiPrompt.trim()}
          className="w-full bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold tracking-wide gap-2"
        >
          <Sparkle size={14} weight="fill" /> {aiLoading ? `Pintando ${grid}×${grid}…` : t("sprite.ai.generate")}
        </Button>
        <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-zinc-600 leading-relaxed">
          {studio?.label || "Sprites"} · {grid}×{grid} · {project.config?.camera === "topdown" ? "TOP-DOWN" : "SIDE"}. La IA pinta en el recuadro. Luego edita y guarda.
        </div>
      </div>
    </div>
  );
}
